import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, UntypedFormControl, Validators } from '@angular/forms';
import {
  StringCustomFieldConfig,
  SharedModule,
  FormInputComponent,
  NotificationService,
  DynamicFormInputComponent
} from '@vendure/admin-ui/core';
import {
  KeyPointListData,
  KeySellingPointData,
  TextAlignment,
  defaultKeyPointListData,
} from '../ui-types';
import { safeJSONParse } from '../utils';
import { notNullOrUndefined } from '@vendure/common/lib/shared-utils';
import { Observable } from 'rxjs';
import { DEFAULT_ACTIVE_LANGUAGE } from '../constants';

@Component({
  template: `
    <vdr-card>
      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-6" label="Text Alignment">
          <select [formControl]="textAlignmentControl">
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </vdr-form-field>
        <vdr-form-field class="clr-col-6" [label]="'Text Color'">
          <input type="color" [formControl]="textColorControl">
        </vdr-form-field>
      </div>
      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-6" label="Title Font Size (px)" hint="Positive integer">
          <input
            type="number"
            min="1"
            step="1"
            [formControl]="titleFontSizeControl"
          />
        </vdr-form-field>
        <vdr-form-field class="clr-col-6" label="Subtitle Font Size (px)" hint="Positive integer">
          <input
            type="number"
            min="1"
            step="1"
            [formControl]="textFontSizeControl"
          />
        </vdr-form-field>
      </div>
      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-6" [label]="'Background Color'">
          <input type="color" [formControl]="backgroundColorControl">
        </vdr-form-field>
        <vdr-form-field class="clr-col-6" [label]="'Icon Size (px)'" hint="Positive integer">
          <input
            type="number"
            [formControl]="iconSizeControl"
            min="1"
            step="1"
          />
        </vdr-form-field>
      </div>
      <div class="clr-row mb-2">
        <vdr-form-field class="clr-col-6" [label]="'Layout'">
          <select [formControl]="keyPointsLayoutTypeControl">
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </select>
        </vdr-form-field>
        <vdr-form-field class="clr-col-6" [label]="'Mobile Layout'">
          <select [formControl]="mobileKeyPointsLayoutTypeControl">
            <option value="horizontal">Horizontal</option>
            <option value="vertical">Vertical</option>
          </select>
        </vdr-form-field>
      </div>
      <vdr-form-field [label]="'List Items'">
        <vdr-dynamic-form-input #keyPointsInput
          [control]="keyPointsControl"
          [def]="keySellingPointInputDef"
        >
        </vdr-dynamic-form-input>
      </vdr-form-field>
    </vdr-card>
  `,
  standalone: true,
  imports: [SharedModule],
})
export class KeyPointListFormComponent
  implements FormInputComponent<StringCustomFieldConfig>, OnInit, AfterViewInit
{
  @ViewChild('keyPointsInput') keyPointsInput: DynamicFormInputComponent;

  readonly: boolean;
  config: StringCustomFieldConfig;
  formControl: FormControl;

  iconSizeControl = new FormControl<number | null>(null, [Validators.min(1),]);
  titleFontSizeControl = new FormControl();
  subtitleFontSizeControl = new FormControl();
  textFontSizeControl = new FormControl();
  titleAlignmentControl = new FormControl();
  textAlignmentControl = new FormControl();
  backgroundColorControl = new FormControl();
  textColorControl = new FormControl();
  keyPointsLayoutTypeControl = new FormControl();
  mobileKeyPointsLayoutTypeControl = new FormControl();
  keyPointsControl = new UntypedFormControl();
  keySellingPointInputDef: any = {
    list: true,
    type: 'string',
    ui: {
      component: 'key-selling-point-form',
      args: {},
    },
  };

  keyPointListData: KeyPointListData;
  private activeLanguageCode = DEFAULT_ACTIVE_LANGUAGE;
  private languageChanges$?: Observable<unknown>;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    const args = (this.config as any)?.ui?.args ?? {};
    if (typeof args.activeLanguageCode === 'string' && args.activeLanguageCode) {
      this.activeLanguageCode = args.activeLanguageCode;
    }
    if (args?.languageChanges$ && typeof args.languageChanges$.subscribe === 'function') {
      this.languageChanges$ = args.languageChanges$ as Observable<unknown>;
    }
    this.keySellingPointInputDef = {
      ...this.keySellingPointInputDef,
      ui: {
        ...this.keySellingPointInputDef.ui,
        args: {
          activeLanguageCode: this.activeLanguageCode,
          languageChanges$: this.languageChanges$,
        },
      },
    };
    this.keyPointListData = safeJSONParse<KeyPointListData>(
      this.formControl.value,
      this.notificationService,
      defaultKeyPointListData,
    )!;
    this.initializeFormControls();
    this.keyPointsLayoutTypeControl.valueChanges.subscribe(this.onKeyPointsLayoutTypeChange);
    this.mobileKeyPointsLayoutTypeControl.valueChanges.subscribe(this.onKeyPointsMobileLayoutTypeChange);
  }

  ngAfterViewInit() {
    this.iconSizeControl.valueChanges.subscribe(this.onIconSizeChange);
    this.titleFontSizeControl.valueChanges.subscribe(this.onTitleFontSizeChange);
    this.subtitleFontSizeControl.valueChanges.subscribe(this.onSubtitleFontSizeChange);
    this.textFontSizeControl.valueChanges.subscribe(this.onTextFontSizeChange);
    this.titleAlignmentControl.valueChanges.subscribe(this.onTitleAlignmentChange);
    this.textAlignmentControl.valueChanges.subscribe(this.onTextAlignmentChange);
    this.backgroundColorControl.valueChanges.subscribe(this.onBackgroundColorChange);
    this.textColorControl.valueChanges.subscribe(this.onTextColorChange);
    this.keyPointsLayoutTypeControl.valueChanges.subscribe(this.onKeyPointsLayoutTypeChange);
    this.mobileKeyPointsLayoutTypeControl.valueChanges.subscribe(this.onKeyPointsMobileLayoutTypeChange);
    if (this.keyPointsInput) {
      this.keyPointsInput.registerOnChange(this.onKeyPointsChange);
      if(this.keyPointListData?.keyPoints) {
        const keyPoints = this.keyPointListData?.keyPoints?.map((value) => JSON.stringify(value)) || [];
        this.keyPointsInput.writeValue(keyPoints);
      }
    }
    this.changeDetector.markForCheck();
  }

  private initializeFormControls() {
    this.iconSizeControl.setValue(this.keyPointListData?.iconSize ?? null);
    this.titleFontSizeControl.setValue(this.keyPointListData?.titleFontSize ?? 32);
    this.subtitleFontSizeControl.setValue(this.keyPointListData?.subtitleFontSize ?? 24);
    this.textFontSizeControl.setValue(this.keyPointListData?.textFontSize ?? 20);
    this.titleAlignmentControl.setValue(this.keyPointListData?.titleAlignment ?? TextAlignment.Center);
    this.textAlignmentControl.setValue(this.keyPointListData?.textAlignment ?? TextAlignment.Center);
    this.backgroundColorControl.setValue(this.keyPointListData?.backgroundColor);
    this.textColorControl.setValue(this.keyPointListData?.textColor);
    this.keyPointsLayoutTypeControl.setValue(this.keyPointListData?.keyPointsLayoutType);
    this.mobileKeyPointsLayoutTypeControl.setValue(this.keyPointListData?.mobileKeyPointsLayoutType);
  }

  private onKeyPointsChange = (values: string[]) => {
    if (this.keyPointListData && JSON.stringify(this.keyPointListData.keyPoints) !== JSON.stringify(values)) {
      const defaultKeySellingPointData: KeySellingPointData = {
          iconId: null,
          title: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
          value: { format: 'plain', i18n: { [this.activeLanguageCode]: '' } },
      };
      this.keyPointListData.keyPoints = values?.map((value) => safeJSONParse<KeySellingPointData>(
        value,
        this.notificationService,
        defaultKeySellingPointData
      )).filter(notNullOrUndefined) ?? [];
      this.updateJsonString();
    }
  };

  private onIconSizeChange = (value: number | null) => {
    if (this.keyPointListData?.iconSize !== value) {
      this.keyPointListData!.iconSize = value ?? undefined;
      this.updateJsonString();
    }
  };

  private onTitleFontSizeChange = (value: number) => {
    if (this.keyPointListData?.titleFontSize !== value) {
      this.keyPointListData!.titleFontSize = value;
      this.updateJsonString();
    }
  };

  private onSubtitleFontSizeChange = (value: number) => {
    if (this.keyPointListData?.subtitleFontSize !== value) {
      this.keyPointListData!.subtitleFontSize = value;
      this.updateJsonString();
    }
  };

  private onTextFontSizeChange = (value: number) => {
    if (this.keyPointListData?.textFontSize !== value) {
      this.keyPointListData!.textFontSize = value;
      this.updateJsonString();
    }
  };

  private onTitleAlignmentChange = (value: TextAlignment) => {
    if (this.keyPointListData?.titleAlignment !== value) {
      this.keyPointListData!.titleAlignment = value;
      this.updateJsonString();
    }
  };

  private onTextAlignmentChange = (value: TextAlignment) => {
    if (this.keyPointListData?.textAlignment !== value) {
      this.keyPointListData!.textAlignment = value;
      this.updateJsonString();
    }
  };

  private onBackgroundColorChange = (value: string) => {
    if (this.keyPointListData?.backgroundColor !== value) {
      this.keyPointListData!.backgroundColor = value;
      this.updateJsonString();
    }
  };

  private onTextColorChange = (value: string) => {
    if (this.keyPointListData?.textColor !== value) {
      this.keyPointListData!.textColor = value;
      this.updateJsonString();
    }
  };

  private onKeyPointsLayoutTypeChange = (value: string) => {
    if (this.keyPointListData?.keyPointsLayoutType !== value) {
      this.keyPointListData!.keyPointsLayoutType = value;
      this.updateJsonString();
    }
  };

  private onKeyPointsMobileLayoutTypeChange = (value: string) => {
    if (this.keyPointListData?.mobileKeyPointsLayoutType !== value) {
      this.keyPointListData!.mobileKeyPointsLayoutType = value;
      this.updateJsonString();
    }
  };

  private updateJsonString() {
    const jsonString = JSON.stringify(this.keyPointListData);
    this.formControl.setValue(jsonString);
    this.formControl.markAsDirty();
  }
}
